import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DownloadIcon, DeleteIcon, ViewIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	Flex,
	HStack,
	Icon,
	SimpleGrid,
	Stack,
	Td,
	Text,
	Tooltip,
	Tr,
	useColorModeValue,
	useDisclosure,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiDownload, FiFileText, FiImage, FiInfo } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "../PropertyModal/shared";
import { removeProtocolAsync } from "../../redux/user/userSlice";
import { API } from "../../utils/http";
import { fullDateFormat } from "../../utils/types";
import Gallery from "../Gallery/Gallery";
import WithConfirmation from "../WithConfirmation/WithConfirmation";

function ProtocolsTableRow(props) {
	const { row, propertyId } = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const textColor = useColorModeValue("gray.700", "white");
	const iconColor = useColorModeValue("blue.500", "blue.300");

	const deleteDocument = (reportId) => {
		console.log("delete: ", { reportId, propertyId });
		dispatch(removeProtocolAsync({ reportId, propertyId }));
	};
	const handleGetDocument = () => {
		const payload = { propertyId, reportId: row.id, name: row.name };
		console.log(payload);
		API.put("get-protocol", payload).then((r) => {
			console.log(r);
			window.open(r.data.url, "_blank");
		});
	};
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [images, setImages] = useState([]);
	const getProtocolPictures = () => {
		const payload = { propertyId, media: row.media, reportId: row.id };
		API.put("get-protocol-picture-urls", payload).then((r) => {
			console.log(r.data);
			setImages(r.data.media);
		});
	};

	const renderViewModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="4xl"
			icon={FiFileText}
			title={t("property.protocolDetailsTitle")}
			subtitle={t("property.viewProtocolSubtitle")}
			footer={<ModalFooterActions onCancel={onClose} cancelLabel={t("misc.close")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.protocolSummary")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4}>
								<HStack alignItems="center">
									<HStack spacing={2} alignItems="center">
										<Icon as={FiFileText} color={iconColor} boxSize={4} />
										<Text fontSize="sm" fontWeight="medium">
											{t("table.name")}:
										</Text>
									</HStack>
									<Text fontSize="sm" fontWeight="bold">
										{row.name}
									</Text>
								</HStack>
								<HStack alignItems="center">
									<HStack spacing={2} alignItems="center">
										<Icon as={FiCalendar} color={iconColor} boxSize={4} />
										<Text fontSize="sm" fontWeight="medium">
											{t("table.addedDate")}:
										</Text>
									</HStack>
									<Text fontSize="sm" fontWeight="medium">
										{moment(row.addedDate).format(fullDateFormat)}
									</Text>
								</HStack>
							</SimpleGrid>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.type")}:
									</Text>
								</HStack>
								<Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
									{t(`types.${row.type}`)}
								</Badge>
							</HStack>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.number")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="medium" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
									{row.number}
								</Text>
							</HStack>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiDownload} title={t("property.protocolDocument")}>
						<Box
							p={3}
							border="1px"
							borderColor="gray.200"
							borderRadius="md"
							_hover={{ bg: "gray.50" }}
							cursor="pointer"
							onClick={() => handleGetDocument(row.id)}
						>
							<HStack justify="space-between">
								<HStack spacing={3}>
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<VStack align="start" spacing={0}>
										<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
											{row.name}
										</Text>
										<Text fontSize="xs" color="gray.500">
											{t("property.clickToDownload")}
										</Text>
									</VStack>
								</HStack>
								<Icon as={DownloadIcon} color={iconColor} boxSize={4} />
							</HStack>
						</Box>
					</PropertyModalSection>
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiImage}
						title={t("property.mediaGallery")}
						headerExtra={
							<Badge colorScheme="blue" fontSize="xs">
								{images.length}
							</Badge>
						}
					>
						{images.length > 0 ? (
							<Box>
								<Gallery items={images} />
							</Box>
						) : (
							<Box textAlign="center" py={6}>
								<Icon as={FiImage} color="gray.300" boxSize={8} mb={3} />
								<Text fontSize="sm" color="gray.500">
									{t("property.noMediaAttached")}
								</Text>
							</Box>
						)}
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Tr>
			<Td minWidth={{ sm: "250px" }} pl="0px">
				<Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
					<Text fontSize="md" color={textColor} minWidth="100%">
						{moment(row.addedDate).format(fullDateFormat)}
					</Text>
				</Flex>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.name}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{t(`types.${row.type}`)}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.number}
				</Text>
			</Td>

			<Td>
				<Stack direction="row">
					<Button
						onClick={() => {
							onOpen();
							getProtocolPictures();
						}}
					>
						<Tooltip label="View">
							<Icon as={ViewIcon} color={"teal.400"} boxSize={6}></Icon>
						</Tooltip>
					</Button>

					<WithConfirmation onConfirm={() => deleteDocument(row.id)}>
						<Button colorScheme={"red"}>
							<Tooltip label="Delete">
								<Icon as={DeleteIcon} boxSize={6}></Icon>
							</Tooltip>
						</Button>
					</WithConfirmation>
					{renderViewModal()}
				</Stack>
			</Td>
		</Tr>
	);
}

export default ProtocolsTableRow;
